import { createContext, type ReactNode, useContext } from "react";

import { SkeletonLoader } from "../SkeletonLoader";

/**
 * Per-resource flags. The table and the stat cards are fed by different requests,
 * so one settling must not un-skeleton the other — clearing both when the first
 * arrives would leave figures on screen that are still the previous query's.
 */
type ByResource = {
  /** The programs table. */
  table: boolean;
  /** The stat card strip. */
  stats: boolean;
};

/**
 * How much of the screen a reload should replace.
 *
 * - `full` — search, a filter, sort, page, or first load. These change *which
 *   rows exist*, so the whole table and every stat card reload.
 * - `period` — the payout period alone. Only the figures that actually move with
 *   it reload (Total earning, Your payout, and the two money stat cards); rows,
 *   entity columns, Last payment (an all-time value, never date-scoped) and the
 *   all-time Clients count stay put.
 *
 * Mutually exclusive per resource: a full reload already covers the
 * period-scoped cells, so flagging both would double up.
 */
export type ReloadScope = {
  full: ByResource;
  period: ByResource;
};

const NOTHING_LOADING: ReloadScope = {
  full: { table: false, stats: false },
  period: { table: false, stats: false },
};

/**
 * Carried by context rather than threaded as props because the cells that care
 * are deep inside TanStack column definitions, and only a handful of them do —
 * passing flags down would mean plumbing them through every cell renderer that
 * doesn't need them.
 */
const ReloadScopeContext = createContext<ReloadScope>(NOTHING_LOADING);

export const ReloadScopeProvider = ({
  scope,
  children,
}: {
  scope: ReloadScope;
  children: ReactNode;
}) => (
  <ReloadScopeContext.Provider value={scope}>
    {children}
  </ReloadScopeContext.Provider>
);

export const useReloadScope = (): ReloadScope => useContext(ReloadScopeContext);

/**
 * Wraps a value whose figure moves with the selected payout period, swapping it
 * for an inline skeleton while that period — and only that period — reloads.
 * Mirrors the canvas's `DateVal`.
 *
 * Renders its children untouched during a *full* reload: the table shows its own
 * skeleton then, so these cells aren't on screen to skeleton.
 *
 * `width` is a percentage of the cell so the skeleton reads as a value of roughly
 * the right length rather than filling the column; the canvas tunes it per column.
 */
export const PeriodValue = ({
  children,
  width = "64%",
  height = 20,
}: {
  children: ReactNode;
  width?: string | number;
  height?: number;
}) => {
  const { period } = useReloadScope();
  if (!period.table) return <>{children}</>;
  // Wrapped: SkeletonLoader takes a closed prop set and drops unknown props, so
  // the test handle has to live on an element it doesn't own.
  return (
    <span data-testid="period-skeleton">
      <SkeletonLoader variant="rounded" width={width} height={height} />
    </span>
  );
};
